import { AUTH_ROUTE_FALLBACK } from "@/constants/auth";

function getNodeChildren(node) {
  if (!node || typeof node !== "object") {
    return [];
  }

  if (Array.isArray(node.children)) {
    return node.children;
  }

  if (Array.isArray(node.z)) {
    return node.z;
  }

  return [];
}

export function normalizeMenuTree(
  nodes = [],
  visited = new WeakSet(),
  ancestorIds = new Set()
) {
  if (!Array.isArray(nodes)) {
    return [];
  }

  return nodes
    .map((node) => {
      if (!node || typeof node !== "object") {
        return null;
      }

      const nodeId = String(node.id || "");
      const hasVisitedNode = visited.has(node);
      const hasAncestorLoop = nodeId && ancestorIds.has(nodeId);

      if (hasVisitedNode || hasAncestorLoop) {
        // 菜单树出现循环引用时直接截断子节点，避免路由守卫递归爆栈。
        return {
          ...node,
          children: [],
        };
      }

      const nextVisited = visited;
      const nextAncestorIds = new Set(ancestorIds);
      nextVisited.add(node);
      if (nodeId) {
        nextAncestorIds.add(nodeId);
      }

      return {
        ...node,
        children: normalizeMenuTree(
          getNodeChildren(node),
          nextVisited,
          nextAncestorIds
        ),
      };
    })
    .filter(Boolean);
}

export function flattenMenuTree(nodes = []) {
  const result = [];
  const visited = new WeakSet();

  function walk(list = [], parentChain = []) {
    list.forEach((item) => {
      if (!item || typeof item !== "object" || visited.has(item)) {
        return;
      }

      visited.add(item);
      const chain = [...parentChain, item];
      result.push({
        ...item,
        children: undefined,
        titleChain: chain.map((node) => node.title).filter(Boolean),
        idChain: chain.map((node) => String(node.id)).filter(Boolean),
      });
      walk(Array.isArray(item.children) ? item.children : [], chain);
    });
  }

  walk(nodes);
  return result;
}

export function collectVisibleMenus(nodes = []) {
  return flattenMenuTree(nodes).filter(
    (item) => item.type !== "action" && Number(item.is_visible) === 1
  );
}

export function findFirstMenuPath(nodes = []) {
  const queue = [...nodes];
  const visited = new WeakSet();

  while (queue.length) {
    const current = queue.shift();
    if (!current || typeof current !== "object" || visited.has(current)) {
      continue;
    }

    visited.add(current);
    if (current.type === "menu" && current.path) {
      return current.path;
    }
    queue.unshift(...(Array.isArray(current.children) ? current.children : []));
  }

  return AUTH_ROUTE_FALLBACK;
}

export function findMenuByPath(nodes = [], path = "") {
  if (!path) {
    return null;
  }

  const flatNodes = flattenMenuTree(nodes);
  const target = flatNodes.find((item) => item.path === path);
  if (!target) {
    return null;
  }

  return {
    ...target,
    navText: target.titleChain.join(" / "),
  };
}

export function getMenuOpenKeys(nodes = [], targetPath = "") {
  const flatNodes = flattenMenuTree(nodes);
  const match = flatNodes.find((item) => item.path === targetPath);
  if (!match) {
    return [];
  }

  return match.idChain.slice(0, -1);
}

export function mapMenuOptions(nodes = [], excludes = []) {
  const excludeSet = new Set(excludes.map((item) => Number(item)));
  const flatNodes = flattenMenuTree(nodes);
  return flatNodes
    .filter((item) => !excludeSet.has(Number(item.id)))
    .map((item) => ({
      id: item.id,
      title: item.titleChain.join(" / "),
      type: item.type,
      parent_id: item.parent_id,
    }));
}

export function findFirstReachableChild(menu) {
  const visited = new WeakSet();

  function walk(node) {
    if (!node || typeof node !== "object" || visited.has(node)) {
      return "";
    }

    visited.add(node);

    if (node.type === "menu" && node.path) {
      return node.path;
    }

    for (const child of Array.isArray(node.children) ? node.children : []) {
      const path = walk(child);
      if (path) {
        return path;
      }
    }

    return "";
  }

  return walk(menu);
}
